/**
 * Handoff status helpers (SPEC-2300).
 *
 * The lane runs one 72-hour `pending` window, so the retired hold /
 * call_in_progress / soft-`expired` buckets never occur on new rows, and
 * `returned_to_remi` is the legacy spelling of `released`. Every read path
 * funnels through here so the rest of the app deals with one vocabulary.
 */

import { HandoffStatus } from '../types/handoff.types';

/** Per-status totals behind the dashboard's Handoffs widget. */
export interface HandoffStatusCounts {
  pending: number;
  claimed: number;
  released: number;
  declined: number;
  expired: number;
}

export const EMPTY_STATUS_COUNTS: HandoffStatusCounts = {
  pending: 0,
  claimed: 0,
  released: 0,
  declined: 0,
  expired: 0,
};

/**
 * Map a raw row status onto the current vocabulary. `returned_to_remi` only
 * appears on rows written before SPEC-2300 and means `released`; a missing
 * status is treated as `pending`, which is where the lifecycle starts.
 */
export const normalizeHandoffStatus = (status: string | null | undefined): HandoffStatus =>
  status === 'returned_to_remi' ? 'released' : ((status || 'pending') as HandoffStatus);

/**
 * Fold a raw status_counts payload into the SPEC-2300 buckets, summing the two
 * spellings of "given back to Remi" and defaulting anything absent to zero.
 */
export const normalizeStatusCounts = (raw: unknown): HandoffStatusCounts => {
  const counts = (raw ?? {}) as Record<string, unknown>;
  const num = (key: string): number => {
    const value = counts[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  };
  return {
    pending: num('pending'),
    claimed: num('claimed'),
    released: num('released') + num('returned_to_remi'),
    declined: num('declined'),
    // The sweep writes `expired`; `hard_expired` is surfaced by the API but
    // has no writer, so it is permanently zero.
    expired: num('expired'),
  };
};
